import React, { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { AddressData } from './types';
import { addressValidationSchema } from './validation';
import { useNomenclator } from '../../hooks';
import { useGooglePlaces } from '../../hooks/useGooglePlaces';
import { ComboboxInput } from './ComboboxInput';

interface AddressFormProps {
  onSubmit: (data: AddressData) => void;
  initialValues?: Partial<AddressData>;
}

export const AddressForm: React.FC<AddressFormProps> = ({ onSubmit, initialValues = {} }) => {
  const { counties, cities, getCitiesForCounty, isLoading: isLoadingNomenclator, error: nomenclatorError } = useNomenclator();
  const { predictions, isLoading: isLoadingPlaces, error: placesError, searchAddress, getAddressDetails } = useGooglePlaces();
  const [showPredictions, setShowPredictions] = useState(false);
  const predictionsRef = useRef<HTMLDivElement>(null);
  const loadedCountiesRef = useRef<Record<number, boolean>>({});

  const formik = useFormik({
    initialValues: {
      judet: initialValues.judet || '',
      localitate: initialValues.localitate || '',
      strada: initialValues.strada || '',
      numar: initialValues.numar || '',
      bloc: initialValues.bloc || '',
      scara: initialValues.scara || '',
      apartament: initialValues.apartament || ''
    },
    validationSchema: addressValidationSchema,
    onSubmit: (values) => {
      onSubmit(values);
    },
  });

  // Handle county change
  const handleCountyChange = async (countyName: string) => {
    formik.setFieldValue('judet', countyName);
    formik.setFieldValue('localitate', ''); // Reset city when county changes

    const county = counties.find(c => c.name === countyName);
    if (county && !loadedCountiesRef.current[county.id]) {
      loadedCountiesRef.current[county.id] = true;
      await getCitiesForCounty(county.id);
    }
  };

  const handleCityChange = (cityName: string) => {
    formik.setFieldValue('localitate', cityName);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (predictionsRef.current && !predictionsRef.current.contains(event.target as Node)) {
        setShowPredictions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleStreetChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    formik.setFieldValue('strada', value);
    
    // Only search if we have a selected county and city
    if (formik.values.judet && formik.values.localitate && value.length >= 3) {
      // Add city and county to search query for better results
      const searchQuery = `${value}, ${formik.values.localitate}, ${formik.values.judet}, Romania`;
      await searchAddress(searchQuery);
      setShowPredictions(true);
    } else {
      setShowPredictions(false);
    }
  };

  const handlePredictionSelect = async (prediction: google.maps.places.AutocompletePrediction) => {
    try {
      const details = await getAddressDetails(prediction.place_id);
      
      // Only update street and number, preserve county and city
      if (details.street) {
        formik.setFieldValue('strada', details.street);
      }
      if (details.number) {
        formik.setFieldValue('numar', details.number);
      }
      
      setShowPredictions(false);
    } catch (error) {
      console.error('Error getting address details:', error);
    }
  };

  const selectedCounty = counties.find(c => c.name === formik.values.judet);
  const availableCities = selectedCounty ? cities[selectedCounty.id] || [] : [];

  const inputClasses = `mt-1 block w-full rounded-md bg-gray-800/90 border-gray-700 text-white placeholder-gray-400
    shadow-sm focus:border-red-500 focus:ring-red-500 text-sm py-2 px-3 transition-colors
    hover:bg-gray-800 focus:bg-gray-800`;

  if (nomenclatorError) {
    return (
      <div className="text-center py-4">
        <p className="text-red-500 text-sm">{nomenclatorError}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 text-sm text-gray-400 hover:text-white"
        >
          Reîncearcă
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-4">
      <ComboboxInput
        id="judet"
        value={formik.values.judet}
        onChange={handleCountyChange}
        onBlur={() => formik.handleBlur('judet')}
        options={counties}
        label="Județ"
        placeholder="Începe să scrii numele județului..."
        error={formik.touched.judet ? formik.errors.judet : undefined}
        disabled={isLoadingNomenclator}
        loading={isLoadingNomenclator && !formik.values.judet}
      />

      <ComboboxInput
        id="localitate"
        value={formik.values.localitate}
        onChange={handleCityChange}
        onBlur={() => formik.handleBlur('localitate')}
        options={availableCities}
        label="Localitate"
        placeholder={formik.values.judet ? "Începe să scrii numele localității..." : "Selectează mai întâi județul"}
        error={formik.touched.localitate ? formik.errors.localitate : undefined}
        disabled={!formik.values.judet || isLoadingNomenclator}
        loading={!!(isLoadingNomenclator && formik.values.judet && !cities[selectedCounty?.id || 0])}
      />

      <div className="relative">
        <label htmlFor="strada" className="block text-sm font-medium text-gray-200">
          Strada
        </label>
        <input
          type="text"
          id="strada"
          {...formik.getFieldProps('strada')}
          onChange={handleStreetChange}
          disabled={!formik.values.judet || !formik.values.localitate}
          className={`${inputClasses}
            ${formik.touched.strada && formik.errors.strada ? 'border-red-500' : 'hover:border-gray-600'}
            ${(!formik.values.judet || !formik.values.localitate) ? 'opacity-50' : ''}`}
          placeholder={formik.values.judet && formik.values.localitate ? 'Începe să scrii pentru sugestii...' : 'Selectează mai întâi județul și localitatea'}
        />
        {isLoadingPlaces && (
          <div className="absolute right-2 top-9">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-500"></div>
          </div>
        )}
        {showPredictions && predictions.length > 0 && (
          <div 
            ref={predictionsRef}
            className="absolute z-10 mt-1 w-full bg-gray-800 rounded-md shadow-lg border border-gray-700 max-h-60 overflow-auto"
          >
            {predictions.map((prediction) => (
              <button
                key={prediction.place_id}
                type="button"
                className="w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-700 focus:outline-none"
                onClick={() => handlePredictionSelect(prediction)}
              >
                {prediction.description}
              </button>
            ))}
          </div>
        )}
        {placesError && (
          <p className="mt-1 text-xs text-red-500">Eroare la căutarea adresei. Te rugăm să introduci adresa manual.</p>
        )}
        {formik.touched.strada && formik.errors.strada && (
          <p className="mt-1 text-xs text-red-500">{formik.errors.strada}</p>
        )}
      </div>

      <div>
        <label htmlFor="numar" className="block text-sm font-medium text-gray-200">
          Număr
        </label>
        <input
          type="text"
          id="numar"
          {...formik.getFieldProps('numar')}
          className={`${inputClasses}
            ${formik.touched.numar && formik.errors.numar ? 'border-red-500' : 'hover:border-gray-600'}`}
        />
        {formik.touched.numar && formik.errors.numar && (
          <p className="mt-1 text-xs text-red-500">{formik.errors.numar}</p>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label htmlFor="bloc" className="block text-sm font-medium text-gray-200">
            Bloc
          </label>
          <input
            type="text"
            id="bloc"
            {...formik.getFieldProps('bloc')}
            className={`${inputClasses} hover:border-gray-600`}
          />
        </div>

        <div>
          <label htmlFor="scara" className="block text-sm font-medium text-gray-200">
            Scara
          </label>
          <input
            type="text"
            id="scara"
            {...formik.getFieldProps('scara')}
            className={`${inputClasses} hover:border-gray-600`}
          />
        </div>

        <div>
          <label htmlFor="apartament" className="block text-sm font-medium text-gray-200">
            Apartament
          </label>
          <input
            type="text"
            id="apartament"
            {...formik.getFieldProps('apartament')}
            className={`${inputClasses} hover:border-gray-600`}
          />
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <button
          type="submit"
          disabled={!formik.isValid || formik.isSubmitting || isLoadingNomenclator}
          className="inline-flex justify-center rounded-md border border-transparent bg-red-500 py-2 px-4 text-sm font-medium text-white 
            shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 
            disabled:opacity-50 transition-colors duration-200 ease-in-out w-full sm:w-auto"
        >
          Continuă
        </button>
      </div>
    </form>
  );
};
